import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Feather, Loader2, Check } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import PasswordInput from "@/components/PasswordInput";

export default function ResetPasswordPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) setErr("Reset link is missing the token.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr(t("auth.password_too_short"));
      return;
    }
    if (password !== confirm) {
      setErr(t("auth.passwords_dont_match"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-cream px-6">
      <form onSubmit={onSubmit} className="w-full max-w-md bg-white rounded-2xl border border-app-border p-6 sm:p-8 space-y-5" data-testid="reset-password-form">
        <div className="flex items-center gap-2 text-olive">
          <Feather className="w-5 h-5" />
          <span className="text-xs font-semibold uppercase tracking-[0.3em]">Aviary Studio</span>
        </div>
        <h1 className="font-heading text-3xl font-semibold text-espresso">{t("auth.reset_password")}</h1>

        {done ? (
          <div className="bg-olive/10 border border-olive/30 text-olive text-sm rounded-lg p-3 flex items-start gap-2" data-testid="reset-done-message">
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>{t("auth.reset_done")}</div>
          </div>
        ) : (
          <>
            <div>
              <Label htmlFor="password">{t("auth.new_password")}</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="mt-1.5"
                testId="reset-password-input"
                testIdSuffix="reset-password"
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label htmlFor="confirm">{t("auth.confirm_password")}</Label>
              <PasswordInput
                id="confirm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                placeholder="••••••••"
                className="mt-1.5"
                testId="reset-confirm-input"
                testIdSuffix="reset-confirm"
                autoComplete="new-password"
              />
            </div>
            {err && (
              <div className="text-sm text-danger bg-danger/10 border border-danger/20 rounded-lg px-3 py-2">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading || !token}
              className="w-full bg-olive hover:bg-olive-hover text-white h-11 rounded-lg"
              data-testid="reset-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.set_new_password")}
            </Button>
          </>
        )}

        <p className="text-sm text-center text-espresso/70">
          <Link to="/login" className="text-olive font-semibold hover:underline">
            ← {t("common.signin")}
          </Link>
        </p>
      </form>
    </div>
  );
}
